import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { ActionDropdown } from '@/components';
import { Button } from '@/design-system/button';
import {
  SegmentedControl,
  SegmentedItem,
} from '@/design-system/segmented-control';
import { TableData, type TableDataColumn } from '@/design-system/table-data';
import { useUrlParam } from '@/hooks/urlState';

import { warehouseApi } from '../../api/warehouse.api';
import { EmptyTable, ListSearch, ListShell } from '../../components/ListShell';
import { HeaderActions } from '../../components/pickers';
import {
  codeCls,
  dateCls,
  LinesQty,
  LoadError,
  MappedBadge,
  metaCls,
  nameCls,
  PageHeader,
} from '../../components/ui';
import { WarehouseFilterPanel } from '../../components/WarehouseFilterPanel';
import { HUB_TRANSFER_FILTERS } from '../../filterFields';
import { fmtDate, fmtDateTime, fmtQty } from '../../format';
import { useListQuery } from '../../hooks/useListQuery';
import { useLoad } from '../../hooks/useLoad';
import { WAREHOUSE_PATHS } from '../../paths';
import {
  HUB_TRANSFER_SIDE_LABEL,
  HUB_TRANSFER_SIDES,
  HUB_TRANSFER_STATUS,
  HUB_TRANSFER_TABS,
} from '../../status';
import type { HubTransferSide, HubTransferSummary } from '../../types';
import { useWarehouse } from '../../WarehouseContext';

type Tab = (typeof HUB_TRANSFER_TABS)[number];

/** "Hub asal → Hub tujuan", the route of one transfer. */
const RouteCell: React.FC<{ t: HubTransferSummary }> = ({ t }) => (
  <div className="flex min-w-0 items-center gap-1.5">
    <span className={`${nameCls} truncate`}>
      {t.hub?.name ?? `Hub #${t.hubID}`}
    </span>
    <i
      className="ri-arrow-right-line shrink-0 text-[14px] text-text-soft-400"
      aria-label="ke"
    />
    <span className={`${nameCls} truncate`}>
      {t.destinationHub?.name ?? `Hub #${t.destinationHubID}`}
    </span>
  </div>
);

const HubTransferListPage: React.FC = () => {
  const navigate = useNavigate();
  const { hubID, ready, refreshToken } = useWarehouse();
  const q = useListQuery<Tab>(HUB_TRANSFER_TABS, 'ALL', HUB_TRANSFER_FILTERS);
  const [side, setSide] = useUrlParam<HubTransferSide>('side', 'ANY', {
    allowed: HUB_TRANSFER_SIDES,
    resetPage: true,
  });
  const { api: filterApi, apiKey: filterKey } = q.filters;
  const search = q.search.committed || null;

  // The page's hub is the source OR the destination ("Semua"), or just one of
  // them; with "Semua hub" there is no side to take.
  const common = {
    hubID,
    ...(hubID !== null ? { side } : {}),
    ...filterApi,
    search,
  };

  const list = useLoad(
    (signal) =>
      warehouseApi.listHubTransfers(
        {
          ...common,
          status: q.tab === 'ALL' ? null : q.tab,
          page: q.page,
          limit: q.limit,
        },
        { signal }
      ),
    [hubID, side, filterKey, search, q.tab, q.page, q.limit, refreshToken],
    ready
  );
  // Tab counts: the list's filters (minus the tab) — never disagree with it.
  const summary = useLoad(
    (signal) => warehouseApi.getHubTransferSummary(common, { signal }),
    [hubID, side, filterKey, search, refreshToken],
    ready
  );

  const tabs = HUB_TRANSFER_TABS.map((k) => ({
    key: k,
    label: k === 'ALL' ? 'Semua' : HUB_TRANSFER_STATUS[k].label,
    count: summary.data
      ? k === 'ALL'
        ? summary.data.total
        : (summary.data.counts[k] ?? 0)
      : null,
  }));

  const columns = useMemo<TableDataColumn<HubTransferSummary>[]>(
    () => [
      {
        key: 'code',
        title: 'ID transfer',
        render: (_v, t) => <span className={codeCls}>{t.code}</span>,
      },
      {
        key: 'client',
        title: 'Client',
        render: (_v, t) => (
          <span className={nameCls}>{t.client?.name ?? `#${t.clientID}`}</span>
        ),
      },
      {
        key: 'route',
        title: 'Rute',
        render: (_v, t) => <RouteCell t={t} />,
      },
      {
        key: 'createdAt',
        title: 'Tanggal pengajuan',
        render: (_v, t) => (
          <span className={dateCls}>{fmtDateTime(t.createdAt)}</span>
        ),
      },
      {
        key: 'createdBy',
        title: 'Pemohon',
        render: (_v, t) => <span className={nameCls}>{t.createdBy}</span>,
      },
      {
        key: 'expected',
        title: 'Estimasi kirim',
        render: (_v, t) => (
          <span className={dateCls}>{fmtDate(t.expectedDeliveryDate)}</span>
        ),
      },
      {
        key: 'lines',
        title: 'Baris / Qty',
        align: 'right',
        render: (_v, t) => (
          <LinesQty lines={t.lineCount} qty={fmtQty(t.totalQty)} />
        ),
      },
      {
        key: 'status',
        title: 'Status',
        render: (_v, t) => (
          <MappedBadge map={HUB_TRANSFER_STATUS} value={t.status} />
        ),
      },
      {
        key: 'actions',
        title: '',
        align: 'right',
        render: (_v, t) => (
          <ActionDropdown
            actions={[
              {
                key: 'view',
                label: 'Lihat detail',
                icon: 'ri-eye-line',
                onClick: () => navigate(`${WAREHOUSE_PATHS.hubTransfer}/${t.id}`),
              },
              ...(t.outboundOrder
                ? [
                    {
                      key: 'order',
                      label: 'Buka outbound order',
                      icon: 'ri-file-list-3-line',
                      onClick: () =>
                        navigate(
                          `${WAREHOUSE_PATHS.outboundRequest}/${t.outboundOrder?.id}`
                        ),
                    },
                  ]
                : []),
              ...(t.transferPurchaseOrder
                ? [
                    {
                      key: 'inbound',
                      label: 'Buka inbound request',
                      icon: 'ri-inbox-archive-line',
                      onClick: () =>
                        navigate(
                          `${WAREHOUSE_PATHS.inboundRequest}/${t.transferPurchaseOrder?.id}`
                        ),
                    },
                  ]
                : []),
            ]}
          />
        ),
      },
    ],
    [navigate]
  );

  return (
    <div className="space-y-5 p-6">
      <PageHeader
        eyebrow="Warehouse · Stock control"
        title="Transfer warehouse"
        description="Permintaan pindah stok client antar hub, dengan dua tahap approval (hub asal, lalu hub tujuan)."
        actions={
          <HeaderActions>
            <Button
              data-testid="hub-transfer-create"
              onClick={() => navigate(`${WAREHOUSE_PATHS.hubTransfer}/new`)}
              leftIcon={<i className="ri-add-line text-[14px]" />}
            >
              Buat transfer
            </Button>
          </HeaderActions>
        }
      />
      <ListShell
        tabs={tabs}
        activeTab={q.tab}
        onTabChange={q.setTab}
        filters={
          <>
            <div className="flex items-center gap-2">
              <span id="hub-transfer-side-label" className={metaCls}>
                Hub ini sebagai
              </span>
              <SegmentedControl
                size="sm"
                value={side}
                onValueChange={(v: string) => {
                  // A toggle group reports '' when the pressed item is pressed again.
                  if (v === 'ANY' || v === 'SOURCE' || v === 'DESTINATION')
                    setSide(v);
                }}
                disabled={hubID === null}
                aria-labelledby="hub-transfer-side-label"
                data-testid="hub-transfer-side"
              >
                {HUB_TRANSFER_SIDES.map((s) => (
                  <SegmentedItem
                    key={s}
                    value={s}
                    data-testid={`hub-transfer-side-${s}`}
                  >
                    {HUB_TRANSFER_SIDE_LABEL[s]}
                  </SegmentedItem>
                ))}
              </SegmentedControl>
            </div>
            <ListSearch
              value={q.search.value}
              onChange={q.search.setValue}
              placeholder="Cari kode, SKU, produk…"
            />
            <WarehouseFilterPanel
              filters={q.filters}
              title="Filter transfer warehouse"
            />
          </>
        }
        pagination={{
          page: q.page,
          limit: q.limit,
          total: list.data?.pagination.total ?? 0,
          onPageChange: q.setPage,
          onLimitChange: q.setLimit,
        }}
      >
        {list.error ? (
          <LoadError message={list.error.message} onRetry={list.reload} />
        ) : (
          <div data-testid="hub-transfer-list">
            <TableData
              size="sm"
              loading={list.loading || !ready}
              data={list.data?.data ?? []}
              columns={columns}
              onRowClick={(t) =>
                navigate(`${WAREHOUSE_PATHS.hubTransfer}/${t.id}`)
              }
              emptyState={<EmptyTable title="Belum ada transfer warehouse" />}
            />
          </div>
        )}
      </ListShell>
    </div>
  );
};

export default HubTransferListPage;
